import { pino } from 'pino';
import path from 'path';
import { RegistryDatabase } from './database/index.js';
import { HealthChecker } from './health-checker.js';
import { ServiceManager } from './service-manager.js';
import { ApiServer } from './api-server.js';
import { RegistryManager } from './registry/registry-manager.js';
import { REGISTRY_DEFAULTS, getDatabasePath, getStoragePath } from './config/defaults.js';
import type { RegistryConfig, DeployedService } from './types.js';

export * from './types.js';
export * from './registry/types.js';
export { RegistryDatabase } from './database/index.js';
export { HealthChecker } from './health-checker.js';
export { ServiceManager } from './service-manager.js';
export { ApiServer } from './api-server.js';
export { RegistryManager } from './registry/registry-manager.js';

/**
 * Codebox Registry - 混合模式实现
 * 1. 服务监控中心（原有功能）
 * 2. 配置注册中心（新功能，受 shadcn/ui registry 启发）
 */
export class CodeboxRegistry {
  private logger = pino({ name: 'codebox-registry' });
  private db: RegistryDatabase;
  private healthChecker: HealthChecker;
  private serviceManager: ServiceManager;
  private registryManager: RegistryManager;
  private apiServer?: ApiServer;
  private config: RegistryConfig;
  private cleanupTimer?: NodeJS.Timeout;

  constructor(config: Partial<RegistryConfig> = {}) {
    // 合并默认配置
    this.config = {
      database: {
        ...REGISTRY_DEFAULTS.database,
        path: config.database?.path || getDatabasePath(),
        ...config.database
      },
      healthCheck: {
        ...REGISTRY_DEFAULTS.healthCheck,
        ...config.healthCheck
      },
      api: {
        ...REGISTRY_DEFAULTS.api,
        ...config.api
      },
      monitoring: {
        ...REGISTRY_DEFAULTS.monitoring,
        ...config.monitoring
      }
    };

    // 初始化组件
    this.db = new RegistryDatabase(this.config.database.path);
    this.healthChecker = new HealthChecker(this.db);
    this.serviceManager = new ServiceManager({
      db: this.db,
      healthChecker: this.healthChecker
    });
    
    // 初始化配置注册管理器
    const registryStoragePath = this.config.database.path 
      ? path.join(path.dirname(this.config.database.path), 'registry')
      : getStoragePath();
    this.registryManager = new RegistryManager((this.db as any).db, registryStoragePath);

    // 初始化 API 服务器
    if (this.config.api.enabled) {
      this.apiServer = new ApiServer(
        this.db,
        this.serviceManager,
        this.healthChecker,
        this.config.api
      );
    }

    // 设置定期清理
    this.setupCleanup();
  }

  // 启动服务注册中心
  async start(): Promise<void> {
    this.logger.info('Starting service registry');

    // 启动 API 服务器
    if (this.apiServer) {
      await this.apiServer.start();
    }

    // 启动所有服务的健康检查
    if (this.config.healthCheck.enabled) {
      await this.healthChecker.startAll();
    }

    this.logger.info('Service registry started');
  }

  // 停止服务注册中心
  async stop(): Promise<void> {
    this.logger.info('Stopping service registry');

    // 停止健康检查
    this.healthChecker.stopAll();

    // 停止清理定时器
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }

    // 关闭数据库
    this.db.close();

    this.logger.info('Service registry stopped');
  }

  // 注册服务
  async registerService(service: DeployedService): Promise<void> {
    this.db.registerService(service);
    
    // 如果服务正在运行且启用了健康检查，启动健康检查
    if (service.status === 'running' && service.healthCheck?.enabled) {
      await this.healthChecker.startHealthCheck(service);
    }
  }

  // 注销服务
  async unregisterService(serviceId: string): Promise<void> {
    this.healthChecker.stopHealthCheck(serviceId);
    this.db.deleteService(serviceId);
  }

  // 获取服务
  getService(serviceId: string): DeployedService | null {
    return this.db.getService(serviceId);
  }

  // 列出服务
  listServices(filter?: any): DeployedService[] {
    return this.db.listServices(filter);
  }

  // 获取统计信息
  getStatistics() {
    return {
      services: this.db.getStatistics(),
      healthChecker: this.healthChecker.getStatus()
    };
  }

  // 服务操作
  async startService(serviceId: string) {
    return await this.serviceManager.startService(serviceId);
  }

  async stopService(serviceId: string) {
    return await this.serviceManager.stopService(serviceId);
  }

  async restartService(serviceId: string) {
    return await this.serviceManager.restartService(serviceId);
  }

  async destroyService(serviceId: string, confirm = false) {
    return await this.serviceManager.destroyService(serviceId, confirm);
  }

  // 获取数据库实例
  getDatabase(): RegistryDatabase {
    return this.db;
  }

  // 获取健康检查器实例
  getHealthChecker(): HealthChecker {
    return this.healthChecker;
  }

  // 获取服务管理器实例
  getServiceManager(): ServiceManager {
    return this.serviceManager;
  }

  // 获取配置注册管理器实例
  getRegistryManager(): RegistryManager {
    return this.registryManager;
  }

  // ========== Registry 功能（新增） ==========
  
  /**
   * 发布配置到 Registry
   */
  async publishConfig(config: any): Promise<any> {
    return await this.registryManager.publish(config);
  }

  /**
   * 获取 Registry 配置
   */
  async getConfig(name: string): Promise<any> {
    return await this.registryManager.get(name);
  }

  /**
   * 搜索 Registry 配置
   */
  async searchConfigs(query: any): Promise<any[]> {
    return await this.registryManager.search(query);
  }

  /**
   * 应用 Registry 配置到本地
   */
  async applyConfig(name: string, options: any): Promise<void> {
    return await this.registryManager.apply(name, options);
  }

  /**
   * 获取 Registry 统计信息
   */
  async getRegistryStats(): Promise<any> {
    return await this.registryManager.getStats();
  }

  // 设置定期清理
  private setupCleanup(): void {
    // 每天清理一次旧数据
    this.cleanupTimer = setInterval(() => {
      this.logger.info('Running cleanup task');
      this.db.cleanupOldData(this.config.monitoring.retentionDays);
    }, 24 * 60 * 60 * 1000);
  }
}

// 为了向后兼容，保留 ServiceRegistry 别名
export const ServiceRegistry = CodeboxRegistry;